// P1 Phase D — panel CONNECTED-load summaries from the canonical electrical
// records (EstimatePanels + EstimateCircuits, db/24). Pure functions; the
// Panel / Circuit schedules (src/config/designSchedules.js) are the first
// consumer. Nothing here is persisted and nothing is a demand or sizing
// calculation:
//   connected VA   = Σ EstimateCircuits.ConnectedVA on the panel (as entered /
//                    generated — the circuit engine's device × VA-per-device)
//   connected amps = VA ÷ (V_LL × √3) for a 3-phase panel, VA ÷ V for
//                    single-phase, using the panel's own Volts text
//   spaces         = Σ Poles vs. the panel's SpaceCount
//   phase loads    = each circuit's VA on the phases of its EXPLICIT
//                    EstimateCircuits.Phase ('A'…'ABC', P1 Phase D-2 product
//                    decision), a multi-pole circuit's VA split equally across
//                    its phases; imbalance = (max phase − mean) ÷ mean
//   demand load    = Σ circuit connected VA × that circuit's DEMAND FACTOR
//                    (P1 Phase D-3 product decision): factor precedence is
//                    the circuit's own EstimateCircuits.DemandFactor
//                    (estimator override) → the project design basis
//                    (TakeoffCriteria elec demand_factor_<load class>) →
//                    the CleanSpace standard (TakeoffVariables
//                    DEMAND_FACTOR_<LOAD CLASS>, db/91) → 1.0; each factor
//                    carries its provenance. Connected, factor and demand are
//                    always three separate values.
// The circuit number is NEVER the source of a phase: a NULL phase is
// reported as unassigned. NEC 220 is deliberately NOT hard-coded as the
// demand engine and nothing here is code-compliant service sizing (a
// 'rule_set' provenance is reserved for an approved implementation); no
// mains size is recommended. Standby/redundancy ≠ demand/diversity.

import { criterionKey } from "../config/takeoffCriteria";

const SQRT3 = Math.sqrt(3);
const num = (v) => { if (v === null || v === undefined || v === "") return null; const n = Number(v); return Number.isFinite(n) ? n : null; };

// '120/208' → { lineToNeutral: 120, lineToLine: 208 }; '480/277' → { 277, 480 };
// '480' → { lineToNeutral: null, lineToLine: 480 }; junk → nulls.
export function parsePanelVoltage(volts) {
  const nums = String(volts ?? "").match(/\d+(?:\.\d+)?/g)?.map(Number).filter(n => n > 0) || [];
  if (!nums.length) return { lineToNeutral: null, lineToLine: null };
  if (nums.length === 1) return { lineToNeutral: null, lineToLine: nums[0] };
  return { lineToNeutral: Math.min(...nums), lineToLine: Math.max(...nums) };
}

// Line current for a balanced connected load at the panel's voltage, or null
// when the voltage is unknown. 3φ: VA / (V_LL √3); 1φ: VA / V_LL (a 120/240
// single-phase panel's line-to-line).
export function connectedAmps(va, volts, phases) {
  const v = parsePanelVoltage(volts).lineToLine;
  const load = num(va);
  if (!v || load === null) return null;
  return +phases === 3 ? load / (v * SQRT3) : load / v;
}

// 'MCB 225A' / 'MLO 400A bus' / 'MCB' — the panel's mains as one phrase.
export function mainsText(panel) {
  const type = panel.MainsType || "MCB";
  const amps = type === "MCB" ? (num(panel.MCBRating) ?? num(panel.MainsRating)) : num(panel.MainsRating);
  return amps ? `${type} ${amps}A${type === "MLO" ? " bus" : ""}` : type;
}

// '120/208V 3φ 4W'
export function voltageText(panel) {
  const bits = [panel.Volts ? `${panel.Volts}V` : null, panel.Phases ? `${panel.Phases}φ` : null, panel.Wires ? `${panel.Wires}W` : null].filter(Boolean);
  return bits.length ? bits.join(" ") : null;
}

// One panel's connected-load summary from its own circuits.
//   { connectedVA, connectedAmps, circuitCount, polesUsed, spaceCount,
//     spacesAvailable, spareCount, byLoadClass: { [LoadClass]: va },
//     warnings: [{ code, message }] }
//   + demandVA, demandAmps, byLoadClass[cls] = { connected, demand, factors: [{ factor, source, count }] },
//     demandBasis: ["REC 1.0 CleanSpace standard", …] — when `ctx` ({ criteria,
//     tunables }) is given; without it every factor is the default 1.0.
export function panelLoadSummary(panel, circuits = [], ctx = {}) {
  const mine = circuits.filter(c => c.PanelID === panel.PanelID);
  const warnings = [];
  let connectedVA = 0, demandVA = 0, polesUsed = 0, spareCount = 0;
  const byLoadClass = {};
  const seen = new Map();
  for (const c of mine) {
    const va = num(c.ConnectedVA) ?? 0;
    const cls = c.LoadClass || "receptacle";
    const df = resolveDemandFactor(c, ctx);
    connectedVA += va;
    demandVA += va * df.factor;
    polesUsed += Math.max(1, num(c.Poles) ?? 1);
    const bucket = (byLoadClass[cls] ||= { connected: 0, demand: 0, factors: [] });
    bucket.connected += va;
    bucket.demand += va * df.factor;
    const f = bucket.factors.find(x => x.factor === df.factor && x.source === df.source);
    if (f) f.count++; else bucket.factors.push({ factor: df.factor, source: df.source, count: 1 });
    if (cls === "spare" || c.TripAmps == null) spareCount++;
    else if (va <= 0) warnings.push({ code: "circuit_no_load", message: `Circuit ${c.CircuitNumber} (${c.CircuitDesc || cls}) has no connected VA.` });
    const n = num(c.CircuitNumber);
    if (n !== null) { seen.set(n, (seen.get(n) || 0) + 1); }
  }
  for (const [n, count] of seen) if (count > 1) warnings.push({ code: "duplicate_circuit_number", message: `Circuit number ${n} is used ${count} times on ${panel.PanelName}.` });
  const spaceCount = num(panel.SpaceCount);
  const spacesAvailable = spaceCount === null ? null : spaceCount - polesUsed;
  if (spacesAvailable !== null && spacesAvailable < 0) warnings.push({ code: "spaces_exceeded", message: `${polesUsed} poles on a ${spaceCount}-space panel.` });
  const amps = connectedAmps(connectedVA, panel.Volts, panel.Phases);
  if (amps === null && mine.length) warnings.push({ code: "voltage_unknown", message: `Panel voltage "${panel.Volts ?? ""}" not recognised — connected amps not computed.` });
  const bus = num(panel.MainsRating) ?? (panel.MainsType === "MCB" ? num(panel.MCBRating) : null);
  if (amps !== null && bus && amps > bus) warnings.push({ code: "connected_exceeds_mains", message: `Connected ${amps.toFixed(0)} A exceeds the ${bus} A mains before any demand factor — review the panel rating or the loads.` });
  const demandAmps = connectedAmps(demandVA, panel.Volts, panel.Phases);
  if (demandAmps !== null && bus && demandAmps > bus && !(amps !== null && amps > bus)) warnings.push({ code: "demand_exceeds_mains", message: `Demand ${demandAmps.toFixed(0)} A exceeds the ${bus} A mains.` });
  const phases = phaseLoads(panel, mine, ctx);
  warnings.push(...phases.warnings);
  const abbrev = { receptacle: "REC", lighting: "LTG", motor: "MTR", equipment: "EQ", hvac: "HVAC", spare: "SPARE" };
  const demandBasis = Object.entries(byLoadClass)
    .filter(([cls, b]) => cls !== "spare" && b.connected > 0)
    .map(([cls, b]) => `${abbrev[cls] || cls} ${b.factors.map(f => `${f.factor} ${DEMAND_SOURCES[f.source].label}${f.count > 1 && b.factors.length > 1 ? ` ×${f.count}` : ""}`).join(" / ")}`);
  return { connectedVA, connectedAmps: amps, demandVA, demandAmps, demandBasis, circuitCount: mine.length, polesUsed, spaceCount, spacesAvailable, spareCount, byLoadClass, phases, warnings };
}

// ---- Demand factors (Connected × Demand Factor, with provenance) -----------------------------
export const DEMAND_LOAD_CLASSES = ["receptacle", "lighting", "motor", "equipment", "hvac"];
export const DEMAND_SOURCES = {
  override: { key: "override", label: "estimator override", mark: "*" },
  project:  { key: "project",  label: "project design basis", mark: "‡" },
  rule_set: { key: "rule_set", label: "approved rule set", mark: "" },   // reserved — no rule set is implemented
  standard: { key: "standard", label: "CleanSpace standard", mark: "" },
  default:  { key: "default",  label: "default 1.0 (no source)", mark: "" },
};
export const demandVariableKey = (loadClass) => `DEMAND_FACTOR_${String(loadClass || "").toUpperCase()}`;
export const demandCriterionKey = (loadClass) => `demand_factor_${String(loadClass || "").toLowerCase()}`;
const inRange = (f) => f !== null && f > 0 && f <= 1.5;

// The demand factor a circuit prices its connected VA at, and where it came
// from. `ctx` = { criteria (flat criterionKey map), tunables (TakeoffVariables
// merged) }. A spare (no breaker / 'spare') is 1.0 with no VA anyway.
//   → { factor, source, sourceLabel, mark, loadClass }
export function resolveDemandFactor(circuit, ctx = {}) {
  const loadClass = circuit?.LoadClass || "receptacle";
  const done = (factor, source) => ({ factor, source, sourceLabel: DEMAND_SOURCES[source].label, mark: DEMAND_SOURCES[source].mark, loadClass });
  const own = num(circuit?.DemandFactor);
  if (inRange(own)) return done(own, "override");
  if (DEMAND_LOAD_CLASSES.includes(loadClass)) {
    const project = num(ctx.criteria?.[criterionKey("elec", demandCriterionKey(loadClass), null)]);
    if (inRange(project)) return done(project, "project");
    const standard = num(ctx.tunables?.[demandVariableKey(loadClass)]);
    if (inRange(standard)) return done(standard, "standard");
  }
  return done(1, "default");
}

// ---- Phase assignment (explicit, persisted on EstimateCircuits.Phase) ----------------------
export const PHASE_ASSIGNMENTS = ["A", "B", "C", "AB", "BC", "CA", "ABC"];
export const PHASE_LETTERS = ["A", "B", "C"];
const PAIRS_3PH = ["AB", "BC", "CA"];

// Assignments a panel can carry: a single-phase panel has A / B / AB only.
export function phaseOptionsFor(panel) {
  return +panel?.Phases === 1 ? ["A", "B", "AB"] : PHASE_ASSIGNMENTS;
}
export const panelPhaseLetters = (panel) => (+panel?.Phases === 1 ? ["A", "B"] : PHASE_LETTERS);

// 'AB' → ['A','B']; anything not a documented assignment → [].
export function phasesOf(phase) {
  const p = String(phase ?? "").trim().toUpperCase();
  return PHASE_ASSIGNMENTS.includes(p) ? p.split("") : [];
}
export const isValidPhase = (phase, panel) => phaseOptionsFor(panel).includes(String(phase ?? "").trim().toUpperCase());

// Per-phase connected load from EXPLICIT assignments only.
//   { byPhase: { A, B, C }, assignedVA, unassignedVA, unassignedCount,
//     invalid: [circuit numbers], maxPhase, imbalancePct, warnings }
export function phaseLoads(panel, circuits = [], ctx = null) {
  const letters = panelPhaseLetters(panel);
  const byPhase = { A: 0, B: 0, C: 0 };
  const byPhaseDemand = { A: 0, B: 0, C: 0 }; // connected × resolved demand factor, same phase split (only when ctx given)
  let assignedVA = 0, unassignedVA = 0, unassignedCount = 0;
  const invalid = [];
  const warnings = [];
  for (const c of circuits.filter(c => c.PanelID === panel.PanelID)) {
    const va = num(c.ConnectedVA) ?? 0;
    const ph = phasesOf(c.Phase);
    const spare = (c.LoadClass === "spare" || c.TripAmps == null) && va <= 0; // a spare with no load needs no phase
    if (!ph.length) { if (!spare) { unassignedVA += va; unassignedCount++; } continue; }
    if (!isValidPhase(c.Phase, panel)) invalid.push(c.CircuitNumber);
    assignedVA += va;
    const factor = ctx ? resolveDemandFactor(c, ctx).factor : 1;
    for (const l of ph) { byPhase[l] += va / ph.length; byPhaseDemand[l] += (va * factor) / ph.length; }
  }
  const loads = letters.map(l => byPhase[l]);
  const mean = loads.reduce((t, v) => t + v, 0) / letters.length;
  const max = Math.max(...loads);
  const maxPhase = mean > 0 ? letters[loads.indexOf(max)] : null;
  const imbalancePct = mean > 0 ? ((max - mean) / mean) * 100 : 0;
  if (unassignedCount) warnings.push({ code: "phase_unassigned", message: `${unassignedCount} circuit${unassignedCount === 1 ? "" : "s"} (${Math.round(unassignedVA).toLocaleString("en-US")} VA) have no phase assignment — not in the per-phase loads.` });
  if (invalid.length) warnings.push({ code: "phase_invalid", message: `Circuit${invalid.length === 1 ? "" : "s"} ${invalid.join(", ")} carry a phase this ${panel.Phases}-phase panel does not have.` });
  return { byPhase, byPhaseDemand, assignedVA, unassignedVA, unassignedCount, invalid, maxPhase, imbalancePct, warnings };
}

// Least-loaded proposal for ONE circuit given the running per-phase loads:
// 1 pole → the lightest single phase; 2 poles → the lightest pair (AB / BC /
// CA on a 3φ panel, AB on 1φ); 3 poles → ABC. Ties resolve in A, B, C /
// AB, BC, CA order so generation is deterministic.
export function proposePhase(poles, panel, running = { A: 0, B: 0, C: 0 }) {
  const letters = panelPhaseLetters(panel);
  const n = Math.max(1, num(poles) ?? 1);
  const sum = (assign) => phasesOf(assign).reduce((t, l) => t + (running[l] || 0), 0);
  const pick = (options) => options.reduce((best, o) => (best === null || sum(o) < sum(best) ? o : best), null);
  if (n >= 3) return letters.length === 3 ? "ABC" : "AB";
  if (n === 2) return letters.length === 3 ? pick(PAIRS_3PH) : "AB";
  return pick(letters);
}

// Fill missing phases on a panel's circuits by least-loaded balancing, in
// the order given; explicit phases are never changed and count toward the
// running loads first. Works on DB rows ({ Phase, Poles, ConnectedVA }) or
// save-payload rows ({ phase, poles, connected_va }) via `keys`.
export const DB_KEYS = { phase: "Phase", poles: "Poles", va: "ConnectedVA" };
export const PAYLOAD_KEYS = { phase: "phase", poles: "poles", va: "connected_va" };
export function proposePhases(rows, panel, keys = DB_KEYS) {
  const running = { A: 0, B: 0, C: 0 };
  const add = (assign, va) => { const ph = phasesOf(assign); for (const l of ph) running[l] += (va || 0) / ph.length; };
  for (const r of rows) if (phasesOf(r[keys.phase]).length) add(r[keys.phase], num(r[keys.va]) ?? 0);
  return rows.map(r => {
    if (phasesOf(r[keys.phase]).length) return r;
    const phase = proposePhase(r[keys.poles], panel, running);
    add(phase, num(r[keys.va]) ?? 0);
    return { ...r, [keys.phase]: phase };
  });
}

// Stable panel order: DisplayOrder, then natural PanelName, then PanelID.
export function comparePanels(a, b) {
  const d = (num(a.DisplayOrder) ?? 0) - (num(b.DisplayOrder) ?? 0);
  if (d !== 0) return d;
  const c = String(a.PanelName ?? "").localeCompare(String(b.PanelName ?? ""), "en", { numeric: true, sensitivity: "base" });
  return c !== 0 ? c : (num(a.PanelID) ?? 0) - (num(b.PanelID) ?? 0);
}
